import React, { useEffect, useContext, useState, useRef, useMemo } from 'react'
import { Box, Button } from '@mui/material'
import ReactPlayer from 'react-player'
import { useTheme } from '@mui/material/styles';
import { debounce } from 'lodash';
import useMediaQuery from '@mui/material/useMediaQuery';
import screenfull from 'screenfull';

/* ⛔ B80 (02/10/2026) : sans API plein ecran des elements (Safari sur iPhone),
   `screenfull` est desactive et ne porte ni `exit` ni `request`. Les appeler
   levait une TypeError qui faisait tomber toute la page : ecran blanc des le
   premier ecran video. On verifie donc `isEnabled` ; sur iPhone, le plein
   ecran passe par la video elle-meme (`webkitEnterFullscreen`, propre a Safari). */
const sortirDuPleinEcran = (element) => {
  if (screenfull.isEnabled && screenfull.isFullscreen) screenfull.exit(element)
}
const passerEnPleinEcran = (element) => {
  if (screenfull.isEnabled) return screenfull.request(element)
  const video = element.querySelector('video')
  if (video && typeof video.webkitEnterFullscreen === 'function') video.webkitEnterFullscreen()
}

import LanguageContext from "../../../contexts/LanguageContext"
import { useTexte } from "../../../contexts/FormeAdresseContext"
import UserContext from "../../../contexts/UserContext"
import { getCurrentUser } from "../../../utils/auth"
import SupportChat from "../../SupportChat"
import { peutLibererVideoEnPanne, estCandidatScan, creerSuiviLecture, annoncerVideoVue, passagesLusParLeNavigateur } from "../../../utils/videoScan"
import APIS from "../../../utils/APIS"
import { signalerVideoPanne } from "../../../utils/signauxScan"

import SuperAdminContext from "../../../contexts/SuperAdminContext"


import { TopBar, BottomBar, CenterContainer } from "./FormatsHelpers"
import { pistesSousTitres, afficherSousTitres } from "../../../utils/sousTitresVideos"
import EditableField from "./EditableField"
import EditableButton from "./EditableButton"

import EditorPopover from "../../ui/Admin/Formations/EditorPopover"

import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import playVideoIcon from "../../../images/playVideoIcon.svg"
import pauseVideoIcon from "../../../images/pauseVideoIcon.svg"
import restartVideoIcon from "../../../images/restartVideoIcon.svg"

const toHHMMSS = (secs) => {
    var sec_num = parseInt(secs, 10)
    var hours   = Math.floor(sec_num / 3600)
    var minutes = Math.floor(sec_num / 60) % 60
    var seconds = sec_num % 60

    return [hours,minutes,seconds]
        .map(v => v < 10 ? "0" + v : v)
        .filter((v,i) => v !== "00" || i > 0)
        .join(":")
}

/* ⛔ POINT 1.5 DE LA FICHE V2 — LA TAILLE DU LECTEUR, MESUREE DANS LE FIGMA.
 *
 * Nawal : « c'est un chiffre a moi et il est approximatif. Je l'ai estime a
 * l'oeil sur un export image, pas mesure dans le fichier. Si tu implementes
 * 55 % et que la maquette dit 60, on aura fige mon erreur. »
 *
 * Mesure faite le 07/09 sur l'export complet du board, au pixel, par balayage
 * de l'image : artboard MacBook Pro 14" de 1512 x 982 (largeur retrouvee a
 * l'unite pres, donc l'export est bien a l'echelle 1), cadre video de
 * 1050 x 620, place a 160 px du haut. ⛔ CETTE MESURE EST FAUSSE, voir la
 * correction du 21/09 plus bas : le node reel fait 1100 x 620.
 *
 * Ce qu'on affichait : 727 x 409 sur la meme fenetre de 1512 x 982. Le lecteur
 * etait donc trop petit de 44 % en largeur. Les « marges trop etroites,
 * dimensions incorrectes » du point 81 partent de la.
 *
 * On pilote par la LARGEUR et non par la hauteur : elle ne depend pas de la
 * hauteur des bandeaux, elle se degrade proprement, et c'est ainsi que sont
 * deja exprimes les autres blocs (image carree a 28 %, champs a 28 et 37 %).
 * `maxHeight` reprend la main sur une fenetre courte.
 *
 * ⚠️ CORRECTION 21/09/2026 — source : fichier Figma lu par l'API, node
 * 3:20294 de la planche 01. Le cadre fait 1100 x 620, pas 1050 x 620. Le 1050
 * venait d'un balayage de l'export JPEG (voir plus haut) : il etait faux de
 * 50 px. Preuve : le node est a x=206 et 206 + 1100 + 206 = 1512 a l'unite.
 * Avec 1050 les marges vaudraient 231, valeur qui n'existe nulle part.
 * Le ratio reel est donc 1100/620 = 1,774, soit du 16/9 a 0,2 % pres, et non
 * 1,694. ⛔ Ne plus remesurer ce cadre sur une image : lire le node. */
/* ⚠️ La part est celle de la LARGEUR DE PAGE, pas de la colonne de contenu :
   1100 sur un artboard de 1512. Rapportee au parent, elle donnait 813 px,
   parce que le conteneur central retire 170 px de marge de chaque cote. */
/* ⛔ LOT B (21/09/2026) — LE CADRE NE DOIT PLUS GROSSIR AVEC LA FENETRE.
   La planche donne 1100 x 620 sur un artboard de 1512. Ce rapport etait
   applique comme une PROPORTION : la video faisait 69 % de la largeur quelle
   qu'elle soit. Mesure au pixel sur LAB : 1048 px a 1512 (69,3 %) et 1329 px a
   1916 (69,4 %) — meme proportion, donc 784 px de haut au lieu de 620, et la
   legende comme le CTA passaient sous la ligne de flottaison.
   La proportion reste utile SOUS 1512, pour retrecir sur les petites fenetres.
   Au-dessus, c'est la taille de la planche qui borne. */
const LARGEUR_VIDEO_PLANCHE = 1100          // px, node 3:20294 lu dans Figma
const PART_LARGEUR_VIDEO = 1100 / 1512      // 0,7275 — ne sert plus qu'en dessous de 1512
const RATIO_CADRE_VIDEO  = 1100 / 620       // 1,774
const FORMAT_CADRE_VIDEO = "1100 / 620"

const FormatVideo = ({ apercuAdmin, actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor, sequenceSubMenu, setSequenceSubMenu }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)
  /* Tutoiement / vouvoiement selon la cible du module (07/10/2026,
     contexts/FormeAdresseContext.js). */
  const t = useTexte()

  const { manageFormatResponse, manageSlide, essai } = useContext(UserContext)

  let superAdminContext = null
  if (isEditor) {
    superAdminContext = useContext(SuperAdminContext)
  }

  const playerRef = useRef(null)

  const [playing, setPlaying] = useState(false)
  const [played, setPlayed] = useState(0)
  const [alreadyPlayed, setAlreadyPlayed] = useState(false)
  // L58/L60 : distinguer "deja vu un jour" (alreadyPlayed, pilote le bouton
  // Continuer) de "lecture entamee / terminee dans cette session"
  const [hasStarted, setHasStarted] = useState(false)
  const [endedSession, setEndedSession] = useState(false)

  /* ⛔ LA VIDEO NE SE DEPLACE PLUS (Nawal, 09/09/2026).
     Historique de ce reglage, parce qu'il a change trois fois :
       — au depart la barre etait bornee au deja-vu. Hicham : « je ne peux
         plus avancer dans le lecteur », et c'etait un mauvais compromis ;
       — on a donc rendu l'avance libre, en ouvrant « Suivant » au temps
         REELLEMENT regarde. Nawal a repondu que les etudiants passeraient
         quand meme, et le banc lui a donne raison ;
       — elle tranche aujourd'hui : on bloque le deplacement. Mais un verrou
         muet est le pire des deux mondes, alors on EXPLIQUE au clic.
     ⚠️ Le blocage ne vaut que pour la PREMIERE lecture. Une fois l'ecran
        valide, on redevient libre : Hicham s'etait plaint de ne pas pouvoir
        revoir une video deja terminee, et le priver du replay ne sert
        aucune pedagogie. */
  const [messagePedago, setMessagePedago] = useState(false)
  useEffect(() => {
    if (!messagePedago) return
    const t = setTimeout(() => setMessagePedago(false), 5000)
    return () => clearTimeout(t)
  }, [messagePedago])
  /* ⛔ LIBRE AUSSI PENDANT UN ESSAI ADMIN (Hicham, 22/09/2026).
     La regle pedagogique reste ENTIERE pour l'apprenant : on ne saute pas une
     video la premiere fois, on la reparcourt librement une fois terminee.
     En essai depuis l'admin, l'ecran n'est JAMAIS « fait » : il faudrait
     regarder chaque video en entier pour atteindre la suivante.
     ⚠️ PREMIERE VERSION FAUSSE : je detectais l'apercu en lisant `vueApprenant`
     dans l'URL. Or « Tester le module » change un ETAT REACT, il ne touche pas
     a l'URL — ce parametre n'existe que pour l'iframe de la vignette. La
     condition etait donc toujours fausse la ou elle devait servir.
     On lit maintenant `essai` du contexte apprenant, qui est vrai exactement
     pendant un essai, quel que soit le chemin emprunte. */
  const deplacementLibre = format?.isDone === true || isEditor === true || essai === true || apercuAdmin === true

  /* ⛔ ET LE PASSAGE AUSSI (Hicham, 22/09/2026 : « je ne peux pas cliquer sur
     suivant, c'est bloque pendant 10 s »).
     « Suivant » ne s'ouvre que lorsque la video a ete REELLEMENT regardee a
     90 % — et sauter n'alimente pas ce compteur, volontairement, pour que les
     etudiants ne passent pas les videos. La regle est juste, et elle reste
     ENTIERE pour l'apprenant.
     Mais debloquer le deplacement sans debloquer le passage ne sert a rien en
     essai : on peut avancer dans la video et rester enferme sur l'ecran. Les
     deux conditions doivent donc se lever ensemble, et pour les memes raisons. */
  const passageLibre = alreadyPlayed || isEditor === true || essai === true || apercuAdmin === true
  const [volume, setVolume] = useState(1)
  const [totalDuration, setTotalDuraton] = useState(0)
  const [playedSecond, setPlayedSecond] = useState(0)
  /* ⛔ LE POINT LE PLUS LOIN REELLEMENT VU. On rend a l'apprenant le droit de
     revenir en arriere — il ne l'avait plus, et rater une phrase devenait
     definitif — sans lui donner celui de sauter a la fin, qui deverrouillerait
     l'ecran sans qu'il ait rien regarde. On ne peut donc se deplacer que dans
     ce qu'on a deja vu. C'est aussi ce que faisait l'ancien lecteur, ou le
     deplacement etait desactive : on y gagne le retour en arriere sans rien
     perdre de la regle. */
  const [maxVu, setMaxVu] = useState(0)
  /* ⛔ CE QUI A ETE REGARDE, PAS CE QUI A ETE ATTEINT. Borner le deplacement au
     point le plus loin vu rendait l'avance impossible : « je ne peux toujours
     pas avancer ». Mais laisser sauter librement deverrouillait l'ecran sans
     que rien n'ait ete regarde, puisque `onEnded` suffisait.
     On separe donc les deux : le deplacement redevient libre, et le passage a
     l'ecran suivant s'appuie sur le TEMPS CUMULE de lecture. Un saut n'ajoute
     rien au compteur, parce qu'on n'additionne que les petits ecarts, ceux
     d'une lecture normale. */
  const [vuReel, setVuReel] = useState(0)
  const refDernierTemps = useRef(0)
  /* ⚠️ 99 % ET NON 90 (decision d'Hicham, 22/09/2026). Le commentaire
     d'origine justifiait 90 par les fondus de fin et le risque d'enfermer
     l'apprenant pour un dixieme de seconde. Hicham tranche a 99 : la marge
     couvre encore l'imprecision des evenements de progression, mais on ne
     valide plus une video dont il reste un dixieme a voir.
     ⚠️ Ce seuil ne concerne QUE l'apprenant. L'editeur, l'essai et l'apercu
     admin passent par `passageLibre`, qui ne le consulte pas. */
  const SEUIL_VU = 0.99
  useEffect(() => {
    if (playerRef?.current) {
      playerRef.current.seekTo(0)
      setPlayed(0)
      setMaxVu(0)
      setVuReel(0)
      refDernierTemps.current = 0
      setPlaying(false)
    }
    suivi.vider() // THE SCAN : chaque ecran repart de zero (100 %, voir utils/videoScan.js)
    annonceEnCours.current = false
    finAtteinte.current = false
    if (format?.isDone) {
      setAlreadyPlayed(true)
    } else {
      setAlreadyPlayed(false)
    }
    setHasStarted(false)
    setEndedSession(false)
  }, [format])

  const theme = useTheme();
  const mediaQueryUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const observedElementRef = useRef(null);
  const modifiedElementRef = useRef(null);

  const innerDimensions = (node) => {
    var computedStyle = getComputedStyle(node)

    let width = node.clientWidth // width with padding
    let height = node.clientHeight // height with padding

    height -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom)
    width -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight)
    return { height, width }
  }
  useEffect(() => {
    if (observedElementRef.current && modifiedElementRef?.current) {
      const observer = new ResizeObserver(debounce((entries) => {
        for (let entry of entries) {
          /* C1 (collision durée↔titre, écrans vidéo) : l'overlay de pré-lecture
             (position:absolute; height:100%) doit se dimensionner sur CETTE boîte,
             pas sur la fenêtre. La boîte porte bien `position:"relative"` en sx,
             mais dans ce @mui/material 5.0.0-beta.5 la règle n'est pas émise
             (position calculée = static, cf. sonde) : l'overlay s'étend alors sur
             toute la hauteur de la fenêtre et son bloc d'infos (aligné en bas)
             vient chevaucher la BottomBar. On rétablit donc le bloc de
             positionnement de façon sûre, en inline, par le même ref que les
             dimensions ci-dessous — l'inline, lui, s'applique de manière fiable. */
          modifiedElementRef.current.style.position = "relative"
          if (mediaQueryUpSm) {
            /* La largeur vient de la maquette, la hauteur en decoule par le
               ratio. L'ancienne version imposait la hauteur a 55 %, une
               estimation que Nawal a elle-meme invalidee.
               ⚠️ On ne peut pas s'en remettre a `maxHeight` en CSS : quand la
               largeur est fixee ET la hauteur bornee, le navigateur ecrase le
               cadre au lieu de reduire la largeur, et le ratio part. Mesure a
               1440x790 : 999 x 552, soit 1,81 au lieu de 1,694. On calcule donc
               la largeur ici, ou l'on connait la hauteur reellement disponible. */
            const st = getComputedStyle(observedElementRef.current)
            const hDispo = observedElementRef.current.clientHeight
                         - parseFloat(st.paddingTop || 0)
                         - parseFloat(st.paddingBottom || 0)
            const largeurMax = modifiedElementRef.current.parentElement
                             ? modifiedElementRef.current.parentElement.clientWidth
                             : window.innerWidth
            /* ⚠️ LA REFERENCE EST L'ECRAN, PAS LA FENETRE (21/09/2026).
               La part de 72,75 % est celle de la LARGEUR DE PAGE. Tant que le
               lecteur occupait toute la fenetre, `window.innerWidth` et la page
               se confondaient. Dans un apercu qui simule 393 px de large, la
               fenetre en fait toujours 1512 : la video serait sortie du cadre.
               On lit donc la racine de l'ecran, et on retombe sur la fenetre si
               elle n'existe pas (rendu hors lecteur). */
            const racine = modifiedElementRef.current.closest("[data-hs-ecran]")
            const largeurPage = racine ? racine.clientWidth : window.innerWidth
            const largeur = Math.min(
              LARGEUR_VIDEO_PLANCHE,              // la planche borne le grand ecran
              largeurPage * PART_LARGEUR_VIDEO,   // et la page borne le petit
              largeurMax,
              hDispo * RATIO_CADRE_VIDEO,
            )
            modifiedElementRef.current.style.width = Math.round(largeur) + "px"
            modifiedElementRef.current.style.height = "unset"
          } else {
            modifiedElementRef.current.style.width = "100%"
            modifiedElementRef.current.style.height = "unset"
          }
        }
      }));
      observer.observe(observedElementRef?.current);
      return () => {
          observer.disconnect();
      };
    }
  }, [observedElementRef?.current, modifiedElementRef?.current, mediaQueryUpSm]);

  /* Filet de securite : si la video n'est toujours pas prete au bout de
     quinze secondes, on libere le passage. Une erreur reseau silencieuse ne
     declenche pas toujours `onError`, et l'apprenant n'a pas a le payer. */
  const [lectureImpossible, setLectureImpossible] = useState(false)
  /* THE SCAN (13/10/2026) : pas de liberation, voir utils/videoScan.js. */
  const [candidatScan] = useState(() => estCandidatScan(getCurrentUser()))
  const [supportOuvert, setSupportOuvert] = useState(false)
  const panneSignalee = useRef(false)
  const [tentative, setTentative] = useState(0)
  useEffect(() => { panneSignalee.current = false }, [format?.id])
  // THE SCAN, video en panne : le cadre n'est plus un bouton. Le panneau et le chat y sont rendus,
  // et un role="button" avalait Espace et Entree (on ne pouvait pas ecrire au support).
  const panneauScan = lectureImpossible && candidatScan
  // THE SCAN, 100 % : suivi seconde par seconde, annonce au serveur, puis seulement « Suivant ».
  const [suivi] = useState(() => creerSuiviLecture())
  const annonceEnCours = useRef(false)
  // 100 % : la fin doit etre atteinte NATURELLEMENT, en plus de chaque seconde entiere lue (vu en vraie lecture Mux le
  // 13/10 : sans cela « Suivant » s'ouvrait a 17,0 s sur 18,7 s, la derniere fraction de seconde pouvait etre sautee).
  const finAtteinte = useRef(false)
  const verifierLecture = () => {
    if (!candidatScan || annonceEnCours.current || !finAtteinte.current) return
    /* Lecture complete = NOS mesures (toutes les secondes lues) OU la liste des passages lus que garde le navigateur
       (elle ne depend pas de nos mesures, que le navigateur peut ralentir). Les deux refusent un saut. */
    const parLeNavigateur = passagesLusParLeNavigateur(playerRef.current?.getInternalPlayer?.())
    if (!suivi.complet(totalDuration) && !(parLeNavigateur && parLeNavigateur.complet)) return
    annonceEnCours.current = true
    annoncerVideoVue(APIS.signauxScan, actFormation?.id, format?.id, suivi.total(totalDuration || parLeNavigateur?.duree)).then((ok) => {
      if (ok) setAlreadyPlayed(true)
      else setTimeout(() => { annonceEnCours.current = false }, 10000) // on reessaiera au prochain passage
    })
  }
  const liberer = () => {
    if (peutLibererVideoEnPanne(getCurrentUser())) setAlreadyPlayed(true)
    // THE SCAN : l'echec est note (une fois par passage sur cet ecran).
    else if (!panneSignalee.current) { panneSignalee.current = true; signalerVideoPanne(format?.id) }
  }
  /* B44 (25/09/2026) : « la lecture n'a jamais demarre » etait aussi vrai d'un
     apprenant qui n'appuie simplement pas sur lecture : au bout de 20 s, la
     video se deverrouillait sans avoir ete vue. On distingue desormais :
     - le lecteur ne s'est jamais declare pret (`onReady`) : panne silencieuse,
       on libere au bout de 20 s, comme avant ;
     - le lecteur est pret et l'apprenant n'a pas demande la lecture : rien a
       liberer, la video attend son geste ;
     - l'apprenant a demande la lecture et rien ne demarre en 20 s : on libere.
       ⚠️ Ce dernier cas est rare avec le vrai lecteur : l'evenement `play` part
       des l'appel a play(), avant la premiere image, et arrete le minuteur. Une
       video qui cale sans erreur APRES le clic n'est donc pas couverte ici
       (relecture du 25/09) ; elle ne l'etait pas davantage avant. */
  const [lecteurPret, setLecteurPret] = useState(false)
  useEffect(() => { setLectureImpossible(false); setLecteurPret(false) }, [format?.id])
  /* 08/10/2026 : piste de sous-titres francais pour les videos qui en ont une
     (utils/sousTitresVideos.js) ; aucune piste pour les autres. La config est
     memorisee : un nouvel objet a chaque rendu ferait recharger le lecteur. */
  const pistes = useMemo(() => pistesSousTitres(format?.playbackId), [format?.playbackId])
  const configLecteur = useMemo(() => ({
    file: { attributes: { playsInline: true, disableremoteplayback: "true", }, tracks: pistes, },
  }), [pistes])
  useEffect(() => {
    if (isEditor || onlyContent || format?.isDone || hasStarted) return
    if (lecteurPret && !playing) return
    const minuteur = setTimeout(() => {
      /* THE SCAN : un onglet ouvert en arriere-plan retarde le chargement des medias ;
         ce n'est pas une panne. On laisse 20 s de plus une fois l'onglet visible. */
      if (candidatScan && document.hidden) { setTentative((n) => n + 1); return }
      /* On ne se fie PAS a la duree rapportee : selon la source, le lecteur
         l'annonce parfois alors que rien n'est lisible. */
      setLectureImpossible(true)
      liberer()
    }, 20000)
    return () => clearTimeout(minuteur)
    /* ⚠️ On depend de `format?.id`, PAS de `format` : l'objet est recree a
       chaque rendu, la dependance changeait donc en permanence, le minuteur
       etait desarme puis rearme sans fin et n'arrivait jamais au bout.
       Mesure au banc : bouton toujours verrouille a 20 secondes. */
  }, [format?.id, format?.isDone, hasStarted, isEditor, onlyContent, lecteurPret, playing, tentative])

  /* THE SCAN (13/10/2026, vu a l'ecran) : le candidat clique sur lecture, rien ne demarre et aucune erreur
     n'arrive. L'evenement `play` part des l'appel a play() et arrete le minuteur ci-dessus : le candidat
     restait devant un cadre noir, sans un mot. Ici : lecture demandee et AUCUN point de progression en 20 s
     (onglet visible) = panne, meme panneau « Rechargez la page ». Une video qui avance, meme lentement, ne
     declenche rien. */
  useEffect(() => {
    if (!candidatScan || isEditor || !playing || playedSecond > 0 || lectureImpossible || format?.isDone) return
    const minuteur = setTimeout(() => {
      if (document.hidden) { setTentative((n) => n + 1); return }
      setPlaying(false)
      setLectureImpossible(true)
      liberer()
    }, 20000)
    return () => clearTimeout(minuteur)
  }, [candidatScan, isEditor, playing, playedSecond, lectureImpossible, tentative, format?.id, format?.isDone])

  /* ⛔ LE DEVERROUILLAGE NE DEPEND PLUS D'ATTEINDRE LA FIN. Il s'appuyait sur
     `onEnded`, donc sauter au bout suffisait — c'est pour cela que l'avance
     etait bloquee. En comptant le temps reellement regarde, on peut rendre le
     deplacement libre sans rien perdre : sauter n'alimente pas le compteur.
     Le seuil est a 90 % et non 100 % : les toutes dernieres secondes d'une
     video sont souvent un fondu ou un logo, et exiger la derniere image
     enfermerait l'apprenant pour un dixieme de seconde manquant. */
  useEffect(() => {
    if (isEditor) return
    // THE SCAN : pas de tolerance a 99 %, c'est la lecture de CHAQUE seconde qui decide (verifierLecture).
    if (candidatScan) return
    if (totalDuration > 0 && vuReel >= totalDuration * SEUIL_VU) {
      setAlreadyPlayed(true)
    }
  }, [vuReel, totalDuration, isEditor, candidatScan])

  /* ⛔ ATTEINDRE LA FIN NE SUFFIT PAS A DEVERROUILLER. Nawal : « en laissant le
     player libre, les etudiants passeront la video ». Elle avait raison, et
     j'avais tort de croire le contraire : j'ai ajoute la regle du temps
     reellement regarde SANS retirer celle-ci. Mesure au banc : 3 s de lecture,
     un saut a 98 %, la fin arrive, `onEnded` s'appelle et « Suivant » s'ouvre.
     La nouvelle regle etait donc contournee par l'ancienne.
     La fin ne libere desormais le passage que si la video a ETE REGARDEE.
     ⚠️ Tolerance a 50 % ici, alors que le seuil normal est a 90 % : les
     evenements de progression peuvent etre etrangles quand l'onglet passe en
     arriere-plan, et quelqu'un qui a vraiment tout regarde ne doit jamais se
     retrouver enferme pour un compteur incomplet. Sauter apres trois secondes
     reste bloque ; regarder en changeant d'onglet ne l'est pas. */
  const onEnded = (e) => {
    setPlaying(false)
    if (candidatScan) {
      // THE SCAN : la fin ne libere rien. La derniere seconde, que les mesures peuvent manquer, compte ;
      // ensuite seule la lecture de toutes les secondes ouvre « Suivant ».
      finAtteinte.current = true
      suivi.marquerLaFin(totalDuration)
      verifierLecture()
    } else if (totalDuration <= 0 || vuReel >= totalDuration * 0.5) {
      setAlreadyPlayed(true)
    }
    setEndedSession(true) // L60 : le replay n'apparait qu'apres une vraie fin
    setHasStarted(false)  // on remontre l'ecran d'infos avec le bouton replay
  }

  useEffect(() => {
    if (viewPlanCours && playing) {
      setPlaying(false)
    }
  }, [viewPlanCours, playing])


  const [viewControls, setViewControls] = useState(false)
  useEffect(() => {
    let int;
    if (playing === true) {
      setViewControls(true)
      int = setTimeout(() => {
        setViewControls(false)
      }, 3000);
    } else {
      const reactPlayer = document.querySelector('.react-player');
      if (reactPlayer) { sortirDuPleinEcran(reactPlayer) }
      setViewControls(false)
    }
    return () => {
      if (int) {
        clearTimeout(int);
      }
    }
  }, [playing]);


  /* EDITOR ADMIN */
  const [editorStep, setEditorStep] = useState(0)

  useEffect(() => {
    if (isEditor) {
      if (editorStep === 1) {
        setPlaying(true)
      } else {
        setPlaying(false)
        playerRef?.current?.seekTo(0)
      }

      if (editorStep === 2) {
        setAlreadyPlayed(true)
      } else {
        setAlreadyPlayed(false)
      }
    }
  }, [editorStep, isEditor])
  /* END EDITOR ADMIN */

  return (
    <Box data-hs-ecran="video" sx={{
      display:"flex",
      flexDirection:"column",
      /* ⚠️ `100vh` vaut la hauteur de la FENETRE, jamais celle d'un conteneur.
         Un apercu qui simule 982 px dans un panneau aurait donc affiche un ecran
         calibre sur la vraie fenetre. La variable `--hs-hauteur-ecran` n'est
         definie QUE par le panneau d'apercu ; partout ailleurs le repli `100vh`
         s'applique et rien ne change. Une variable CSS plutot qu'un prop : neuf
         signatures de composants restent intactes, et un format rendu hors du
         lecteur se comporte correctement sans qu'on ait pense a le lui dire. */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      /* ⛔ LE FORMAT DEVIENT SON PROPRE REFERENTIEL DE MESURE (08/09/2026).
         Nawal voit « de grosses differences » entre l'apercu de l'admin et la
         vue apprenant. Cause : l'apercu ne rend pas l'ecran a la taille de la
         fenetre. `ScaleThumbForm` le rend dans une boite FIXE de 1500 x 1000,
         puis la reduit par un `transform: scale()`. Or `vw` et `vh` se
         calculent sur la FENETRE, jamais sur cette boite : les proportions
         divergeaient donc des deux cotes, sans qu'aucun reglage puisse les
         reconcilier — ce sont deux referentiels differents.
         En declarant ce conteneur, les unites `cqw`/`cqh` employees plus bas se
         mesurent sur LUI. Dans le lecteur il occupe la fenetre : le rendu ne
         bouge pas d'un pixel. Dans l'apercu il fait 1500 x 1000 : le rendu
         devient fidele. */
      containerType:"size",
      overflow:"hidden",
      /* ⛔ Pas de gap racine : la planche colle le contenu au bandeau. */
    }}>
      { /* EDITOR ADMIN */}
      {isEditor &&
        <EditorPopover
          open={sequenceSubMenu}
        >
          <Box sx={{marginBottom:"2px", fontWeight:"bold"}}>Étapes</Box>
          <Box sx={{border:"1px solid #2929292f", padding:"5px"}}>
            <Box sx={{display:"flex", gap:"10px", alignItems:"center"}}>
              <Box
                onClick={() => {setEditorStep(0)}}
                sx={{padding:"0px 5px", height:"20px", background:editorStep===0?"red":"black", color:"white", fontSize:"16px", fontWeight:"bold", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer"}
              }>
                Début
              </Box>
              <Box
                onClick={() => {setEditorStep(1)}}
                sx={{padding:"0px 5px", height:"20px", background:editorStep===1?"red":"black", color:"white", fontSize:"16px", fontWeight:"bold", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer"}
              }>
                Lecture
              </Box>
              <Box
                onClick={() => {setEditorStep(2)}}
                sx={{padding:"0px 5px", height:"20px", background:editorStep===2?"red":"black", color:"white", fontSize:"16px", fontWeight:"bold", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer"}
              }>
                Fin
              </Box>
            </Box>
          </Box>
          <Box sx={{marginTop:"10px", marginBottom:"2px", fontWeight:"bold"}}>Vidéo</Box>
          <Box sx={{border:"1px solid #2929292f", padding:"5px"}}>
            <Box sx={{display:"flex", gap:"5px", alignItems:"center"}}>
              <Box>
                Url&nbsp;:
              </Box>
              <Box sx={{flex:1}}>
                <input
                  value={format?.playbackId}
                  style={{width:"100%"}}
                  onChange={(e) => {
                    superAdminContext?.onSequenceChange(
                      'playbackId',
                      e.target.value,
                      superAdminContext?.adminPlayerIndexes
                    )
                  }}
                />
              </Box>
            </Box>
          </Box>
        </EditorPopover>
      }
      { /* END EDITOR ADMIN */}
      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />
      <CenterContainer
        /* ⚠️ 80 px et non 120 : le cadre video est pose a y=80 dans le cadre de
           contenu (node 3:20294). Mesure sur LAB avant correction : y=246 au lieu
           de 155, soit 91 px de trop. */
        retraitHaut={[24, 160, 80, 80]}
        actFormation={actFormation}
        actIndex={actIndex}
        noPartInfos
        passRef={observedElementRef}
        onlyContent={onlyContent}
      >
        {/* ⛔ BLOC RETIRE LE 19/09/2026 — TITRE ET LEGENDE EN DOUBLE SUR MOBILE.
            Un bloc <Hidden smUp> rendait ici, AU-DESSUS du lecteur, le titre,
            le texte et la duree. Il datait d'avant le redesign, quand seul le
            mobile portait ces informations. La rangee du redesign, sous le
            lecteur, les rend desormais a TOUTES les tailles : sur mobile les
            deux s'affichaient donc, l'un au-dessus, l'autre en dessous.
            Ce n'est pas un defaut de point de rupture, c'est un proprietaire
            en trop — on retire le bloc au lieu de le masquer en CSS.
            FIGMA EXACT, node `3:40458` (Mobile-Video avant lecture lancee) :
            le lecteur y est a y=172 et le titre + description a y=411, donc
            SOUS le lecteur. Rien n'est prevu au-dessus. */}
        <Box
          ref={modifiedElementRef}
          sx={{
            aspectRatio: FORMAT_CADRE_VIDEO,
            width:"100%",
            /* La largeur exacte est posee par l'observateur ci-dessus, qui
               connait la hauteur disponible. Ici on ne garde que la borne
               horizontale, pour le cas ou l'observateur n'a pas encore tourne. */
            maxWidth:"100%",
            margin:"0 auto",
            background:(playing || hasStarted) ? "transparent" : "url('https://image.mux.com/"+format?.playbackId +"/thumbnail.png?width=1280&height=720&fit_mode=crop')",
            backgroundRepeat:"no-repeat",
            backgroundSize:"cover",
            position:"relative",
            cursor:panneauScan ? "default" : "pointer",
          }}
          onClick={panneauScan ? undefined : () => {
            if (isEditor !== true) {
              setPlaying(!playing)
            }
          }}
          /* B44, suite (25/09/2026) : la video doit se lancer au CLAVIER aussi.
             Depuis que le repli de 20 s ne libere plus une video prete non
             lancee, un apprenant sans souris (lecteur d'ecran, motricite)
             serait reste enferme devant l'ecran. Tab puis Entree ou Espace. */
          tabIndex={isEditor === true || panneauScan ? -1 : 0}
          role={panneauScan ? undefined : "button"}
          aria-label={panneauScan ? undefined : (playing ? "Mettre la vidéo en pause" : "Lire la vidéo")}
          onKeyDown={panneauScan ? undefined : (e) => {
            if (isEditor === true) return
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPlaying(!playing) }
          }}
        >
          {(onlyContent !== true || isEditor === true) &&
            <ReactPlayer
              className="react-player"
              loop={false}
              /* ⛔ `disableRemotePlayback` etait passe a ReactPlayer lui-meme,
                 qui ne le connait pas et le repercute sur son <div> : d'ou
                 l'avertissement React « does not recognize the prop », affiche
                 en console a chaque ecran video, et surtout un attribut qui
                 n'atteignait jamais la balise <video>. Sa place est dans les
                 attributs du lecteur de fichier, avec playsInline. */
              /* React 16 ne connait pas cet attribut media et refuse la forme
                 camelCase : il demande explicitement la forme minuscule, qui
                 est celle du HTML. Ecrit ainsi, il atteint la balise <video>
                 et la console redevient muette. */
              config={configLecteur}
              url={'https://stream.mux.com/' + format?.playbackId + '.m3u8'}
              volume={volume}
              width="100%"
              height="100%"
              style={{
                overflow:"hidden",
                //aspectRatio:"16/9",
                position:"absolute",
                visibility:(playing || hasStarted)?"visible":"hidden", // L58
                top:0,
                left:0,
              }}
              onReady={() => {
                setLecteurPret(true)
                if (candidatScan) setLectureImpossible(false) // THE SCAN : le lecteur a fini par repondre
                /* 08/10/2026 : sous-titres francais, voir utils/sousTitresVideos.js */
                if (pistes.length) afficherSousTitres(playerRef.current?.getInternalPlayer?.())
              }}
              onPlay={() => { setHasStarted(true); if (candidatScan) { setLectureImpossible(false); finAtteinte.current = false /* chaque (re)demarrage doit aller jusqu'a sa fin : un saut a la fin avant ne compte pas */ } }}
              onPause={(e) => {
                setPlaying(false)
              }}
              onError={(e, donnees) => {
                /* hls.js remonte aussi ses erreurs PASSAGERES (tampon vide un
                   instant, segment relance) avec `fatal: false` : il s'en remet
                   seul. Les traiter comme une panne liberait la video en pleine
                   lecture sur une connexion instable (relecture du 25/09). Une
                   erreur sans ce detail (autre source) reste traitee comme avant. */
                if (donnees && donnees.fatal === false) return
                /* ⛔ NE JAMAIS ENFERMER L'APPRENANT. Le bouton « Suivant » ne
                   se deverrouillait qu'a la fin de la video (`alreadyPlayed`).
                   Si la video ne se lit pas, il n'y avait AUCUNE sortie : ni
                   bouton, ni message. Trouve au banc sur « Boost Emotional
                   Intelligence », bloque des l'ecran 1 sur 133.
                   Le cas se produit vraiment : sur les 100 videos du
                   catalogue, une ne repond plus cote Mux (400), et une seule
                   suffit a bloquer un module entier. S'y ajoutent les pannes
                   reseau, un lecteur qui n'initialise pas, un onglet en
                   arriere-plan.
                   ⚠️ J'avais d'abord annonce DIX-HUIT videos mortes : mon
                   comptage traitait des lignes vides comme des identifiants et
                   appelait Mux sans identifiant. Filtrer les valeurs vides
                   AVANT de compter, et se mefier d'un chiffre qui tombe rond.
                   Une erreur de lecture libere donc le passage, et le dit. */
                setPlaying(false)
                setLectureImpossible(true)
                liberer()
              }}
              onEnded={onEnded}
              playing={playing}
              onDuration={(duration) => {
                setTotalDuraton(duration)
              }}
              /* ⚠️ RELEVE TOUTES LES 400 ms, ET NON TOUTES LES SECONDES.
                 Le compteur ci-dessous n'additionne que les ecarts de moins de
                 deux secondes, pour distinguer la lecture du saut. Au rythme
                 par defaut de ReactPlayer — une seconde — une lecture acceleree
                 produit des ecarts qui depassent ce seuil et cesse d'etre
                 comptee : l'apprenant regarderait vraiment sans jamais
                 deverrouiller. Constate au banc le 09/09/2026. A 400 ms,
                 l'ecart vaut 0,4 s en lecture normale : il reste sous le seuil
                 jusqu'a quatre fois la vitesse, alors qu'un saut, lui, se
                 compte toujours en secondes. */
              progressInterval={400}
              onProgress={(e) => {
                setPlayed(e.played * 100)
                setPlayedSecond(e.playedSeconds)
                setMaxVu((v) => Math.max(v, e.played * 100))
                /* ⚠️ Un ecart superieur a deux secondes n'est pas de la lecture :
                   c'est un saut, ou un onglet revenu au premier plan. On ne le
                   compte pas, sinon deplacer le curseur suffirait a « voir »
                   la video. */
                const ecart = e.playedSeconds - refDernierTemps.current
                refDernierTemps.current = e.playedSeconds
                if (ecart > 0 && ecart < 2) {
                  setVuReel((v) => v + ecart)
                  if (candidatScan) { suivi.marquer(e.playedSeconds); verifierLecture() }
                }
                if (e.played * 100 >= 100) {
                  onEnded(e)   // qui verifie desormais le temps reellement vu
                }
              }}
              ref={playerRef}
            />
          }
          <Box sx={{
            //background:"rgba(0,0,0,0.4)",
            position:"absolute",
            top:0,
            left:0,
            width:"100%",
            height:"100%",
            /* ⛔ ELLE DISPARAISSAIT A LA PAUSE. `display: playing ? flex : none`
               retirait la barre au moment precis ou l'apprenant en a besoin :
               une fois en pause, plus de bouton lecture, plus de progression,
               plus rien — il fallait deviner qu'un clic sur l'image relance.
               Elle reste desormais des que la video a demarre. */
            display:(playing || hasStarted) ? "flex" : "none",
            /* ⛔ A L'ARRET, LA BARRE NE DOIT PAS S'EVANOUIR. `viewControls`
               retombe a faux trois secondes apres le debut de la lecture : en
               pause, la barre restait donc affichee mais TRANSPARENTE, et il
               fallait retrouver a la souris une zone qu'on ne voyait pas.
               Elle ne s'efface que pendant la lecture, ou elle gene. */
            opacity:(!playing || viewControls) ? 1 : 0,
            //opacity:1,
            color:"white",
            padding:"25px",
            gap:"25px",
            justifyContent:"center",
            alignItems:"center",
            "&:hover" : {
              opacity:1
            }
          }}>
            {/* ⛔ POURQUOI LE CURSEUR NE BOUGE PAS. Un verrou muet se lit comme
                une panne : l'apprenant clique, rien ne se passe, il conclut que
                le lecteur est casse. La phrase dit la regle et sa raison, une
                fois, puis s'efface au bout de cinq secondes. */}
            {messagePedago &&
              <Box sx={{
                position:"absolute",
                left:"50%",
                bottom:"88px",
                transform:"translateX(-50%)",
                maxWidth:"min(560px, 80%)",
                padding:["10px 14px", "12px 18px"],
                borderRadius:"6px",
                background:"rgba(10,10,11,0.92)",
                color:"#FFFFFF",
                fontSize:["13px", "15px"],
                lineHeight:"140%",
                textAlign:"center",
                userSelect:"none",
                pointerEvents:"none",
                zIndex:4,
              }}>
                {/* Point 1.4 (review du 5 oct) : texte de Nawal, dans le
                    dictionnaire ; meme place, meme moment. */}
                {t(languageValues?.formation?.videoSansAvance)}
              </Box>
            }
            {/* ⚠️ Cette grosse icone signifie « cliquez pour mettre en pause ».
                Avant, elle n'existait que pendant la lecture. Depuis que la
                barre reste affichee a l'arret, elle s'y affichait aussi — une
                icone de pause sur une video DEJA en pause, au milieu de
                l'image. Elle redevient ce qu'elle est : un signe de lecture. */}
            {playing &&
              <Box sx={{width:["55px", "70px", "70px", "70px"], minWidth:["55px", "70px", "70px", "70px"], userSelect:"none",}}>
                <img src={pauseVideoIcon} style={{width:"100%"}} />
              </Box>
            }
            {/* L59 : controles refaits d'apres l'export Figma de Valentin —
                disques gris fonce a pictos blancs, progression blanche sur
                fond sombre, temps "ecoule - total", pas de bandeau. */}
            <Box onClick={(e) => {e.stopPropagation()}} sx={{
              position:"absolute",
              left:0,
              width:"100%",
              bottom:0,
              display:"flex",
              gap:["10px", "15px", "15px", "18px"],
              alignItems:"center",
              padding : ["10px 10px", "14px 20px"],
              cursor:"default",
            }}>
              <Box
                onClick={(e) => { e.stopPropagation(); setPlaying(!playing) }}
                sx={{
                  width:["34px","40px"], height:["34px","40px"], minWidth:["34px","40px"],
                  borderRadius:"50%", background:"rgba(47,47,47,0.85)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer",
                  "&:hover":{ background:"rgba(47,47,47,1)" },
                }}
              >
                {playing ? (
                  <svg width="12" height="14" viewBox="0 0 12 14"><rect x="1" width="3.6" height="14" fill="white"/><rect x="7.4" width="3.6" height="14" fill="white"/></svg>
                ) : (
                  <svg width="12" height="14" viewBox="0 0 12 14"><path d="M0 0 L12 7 L0 14 Z" fill="white"/></svg>
                )}
              </Box>
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  if (!deplacementLibre) {
                    setMessagePedago(true)
                    return
                  }
                  const r = e.currentTarget.getBoundingClientRect()
                  if (!r.width || !playerRef.current) return
                  const vise = ((e.clientX - r.left) / r.width) * 100
                  const cible = Math.max(0, Math.min(vise, 100))
                  playerRef.current.seekTo(cible / 100, "fraction")
                  setPlayed(cible)
                }}
                sx={{
                  flex:1,
                  height:"5px",
                  background:"rgba(10,10,11,0.75)",
                  position:"relative",
                  /* Le curseur dit la verite : rien a saisir tant que la
                     premiere lecture n'est pas faite. */
                  cursor: deplacementLibre ? "pointer" : "not-allowed",
                  /* une zone de clic de 5 px se rate ; on l'elargit sans
                     epaissir le trait */
                  "&::before": {
                    content:'""', position:"absolute", left:0, right:0,
                    top:"-9px", bottom:"-9px",
                  },
                }}>
                {/* Ce qui a ete REELLEMENT regarde. La bande se remplit a mesure
                    qu'on regarde, et le bouton « Suivant » s'ouvre quand elle est
                    presque pleine : le verrou cesse d'etre inexplicable pour qui
                    a saute jusqu'a la fin. */}
                <Box sx={{
                  position:"absolute", top:0, left:0, height:"100%",
                  width:(format?.isDone || !totalDuration ? 100
                         : Math.min(100, (vuReel / totalDuration) * 100)) + "%",
                  background:"rgba(255,255,255,0.38)",
                }} />
                <Box sx={{
                  position:"absolute", top:0, left:0, height:"100%",
                  width:played+"%",
                  background:"white",
                }} />
              </Box>
              <Box sx={{color:"white", fontSize:["13px","15px"], whiteSpace:"nowrap", userSelect:"none"}}>
                {(totalDuration >= 0 && playedSecond >= 0) ? (
                  <>{toHHMMSS(playedSecond)} - {toHHMMSS(totalDuration)}</>
                ) : (
                  "00:00"
                )}
              </Box>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (volume === 1) { setVolume(0) } else { setVolume(1) }
                }}
                sx={{
                  width:["34px","40px"], height:["34px","40px"], minWidth:["34px","40px"],
                  borderRadius:"50%", background:"rgba(47,47,47,0.85)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer",
                  "&:hover":{ background:"rgba(47,47,47,1)" },
                }}
              >
                <svg width="16" height="14" viewBox="0 0 16 14">
                  <path d="M0 4.5 H3.5 L8 0.5 V13.5 L3.5 9.5 H0 Z" fill="white"/>
                  {volume === 1 ? (
                    <path d="M10.5 3.5 C12 5 12 9 10.5 10.5 M12.5 1.5 C15 4 15 10 12.5 12.5" stroke="white" strokeWidth="1.6" fill="none" strokeLinecap="round"/>
                  ) : (
                    <path d="M10.5 5 L15 9 M15 5 L10.5 9" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
                  )}
                </svg>
              </Box>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  const reactPlayer = document.querySelector('.react-player');
                  if (reactPlayer) { passerEnPleinEcran(reactPlayer) }
                }}
                data-plein-ecran
                sx={{
                  width:["34px","40px"], height:["34px","40px"], minWidth:["34px","40px"],
                  borderRadius:"50%", background:"rgba(47,47,47,0.85)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer",
                  "&:hover":{ background:"rgba(47,47,47,1)" },
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="1.8">
                  <path d="M1 4.5 V1 H4.5 M9.5 1 H13 V4.5 M13 9.5 V13 H9.5 M4.5 13 H1 V9.5"/>
                </svg>
              </Box>
            </Box>
          </Box>
          {/* Un mot, sobre, quand la video ne peut pas etre lue : sans lui
              l'apprenant verrait un cadre inerte et un bouton qui s'active
              tout seul, sans comprendre pourquoi. */}
          {lectureImpossible && candidatScan &&
            /* Petit ecran (vu sur iPhone 13 le 13/10) : le cadre video ne fait qu'environ 200 px de haut, le texte et
               les deux boutons y etaient coupes. On compacte (textes et boutons plus petits), on opaque davantage (le
               bouton de lecture ne doit plus transparaitre) et on centre « en toute securite » : si le contenu depasse,
               le cadre defile au lieu de couper le haut. */
            <Box sx={{
              position:"absolute", inset:0, zIndex:5,
              display:"flex", flexDirection:"column",
              overflowY:"auto",
              padding:["6px 10px", "24px"],
              background:"rgba(10,10,11,0.94)",
              color:"white",
              textAlign:"center",
            }}>
              <Box sx={{margin:"auto", display:"flex", flexDirection:"column", alignItems:"center", gap:["5px", "14px"], maxWidth:"560px"}}>
                <Box sx={{fontSize:["12px", "16px", "18px"], lineHeight:1.3}}>
                  {t(languageValues?.formation?.videoScanPanne)}
                </Box>
                <Button variant="contained" onClick={() => window.location.reload()} sx={{fontSize:["12px", "14px"], padding:["3px 12px", "6px 16px"], minHeight:0}}>
                  {t(languageValues?.formation?.videoScanRecharger)}
                </Button>
                <Box sx={{fontSize:["11px", "14px"], opacity:0.85, marginTop:["2px", "6px"]}}>
                  {t(languageValues?.formation?.videoScanSupportAide)}
                </Box>
                <Button variant="outlined" color="inherit" onClick={() => setSupportOuvert(true)} sx={{fontSize:["12px", "14px"], padding:["3px 12px", "6px 16px"], minHeight:0}}>
                  {t(languageValues?.formation?.videoScanSupport)}
                </Button>
              </Box>
            </Box>
          }
          {supportOuvert && candidatScan &&
            <SupportChat plateforme="scan" ouvertParDefaut onClose={() => setSupportOuvert(false)} />
          }
          {candidatScan && endedSession && !alreadyPlayed && !lectureImpossible &&
            <Box sx={{
              position:"absolute",
              left:0, right:0, bottom:0,
              padding:["10px 14px", "12px 18px"],
              background:"rgba(10,10,11,0.72)",
              color:"white",
              fontSize:["13px", "14px"],
              textAlign:"center",
              pointerEvents:"none",
            }}>
              {t(languageValues?.formation?.videoScanToutRegarder)}
            </Box>
          }
          {lectureImpossible && !candidatScan &&
            <Box sx={{
              position:"absolute",
              left:0, right:0, bottom:0,
              padding:["10px 14px", "12px 18px"],
              background:"rgba(10,10,11,0.72)",
              color:"white",
              fontSize:["13px", "14px"],
              textAlign:"center",
              pointerEvents:"none",
            }}>
              {t(languageValues?.formation?.videoIndisponible)}
            </Box>
          }
          {/* ⛔ PD-18 (Hicham, 02/10/2026) : « comme la maquette ». Une fois la
              video lancee, plus de bouton au centre, meme en pause : la barre
              du bas porte la lecture (planche 3:20309). Le bouton central a la
              pause venait de la relecture du 28/07 (L58) ; sur telephone il
              doublait celui de la barre (photo d'Hicham, iPhone, 02/10). Le
              bouton central reste AVANT le premier lancement (3:40458). */}
          <Box sx={{
            /* ⛔ L53, L54 ET L55 SONT SUPERSEDEES PAR LE FIGMA DU 17/09.
               Les trois sont PUREMENT VISUELLES — un alignement, un degrade, une
               opacite au survol — et aucune ne touche au comportement : ni la
               reponse, ni la donnee enregistree, ni la progression, ni le verrou
               video, qui vit ailleurs et reste intact.
               La planche 01 montre un lecteur PROPRE, bouton de lecture centre,
               et la legende SOUS la video sur fond blanc. Le degrade n'avait de
               raison d'etre que pour rendre lisible un texte pose sur l'image ;
               ce texte n'y est plus, le degrade non plus. */
            background:"transparent",
            position:"absolute",
            top:0,
            left:0,
            width:"100%",
            height:"100%",
            display:(playing || hasStarted) ? "none" : "flex",
            color:"white",
            padding:"40px", // L57
            gap:"25px",
            justifyContent:"center",
            alignItems:"center",
            /* L55 devient un simple etat de survol du bouton, sans le voile. */
            /* ⚠️ 21/09/2026 : la planche pose le bouton OPAQUE (noeud 3:20295,
               disque blanc plein, aucune opacite declaree). Le repos a 85 %
               venait de l'ancienne icone sombre, qu'il fallait attenuer. Avec un
               disque blanc, l'attenuation le fait disparaitre sur une image
               claire. On garde la transition au survol, qui est un affordance
               et non une valeur de maquette. */
            /* Atténuation au repos : elle va avec l'icone SOMBRE, retablie le
               21/09 (voir l'en-tete de playVideoIcon.svg). */
            "& .hs-play-icon" : { opacity:0.85, transition:"opacity .2s" },
            "&:hover .hs-play-icon" : { opacity:1 },
          }}>
            {/* La legende n'est plus dans le lecteur : elle est passee SOUS lui,
                sur fond blanc, comme la planche 01 le montre. */}
            <Box className="hs-play-icon" sx={{width:["55px", "70px", "70px", "70px"], minWidth:["55px", "70px", "70px", "70px"], userSelect:"none",}}>
              <img src={endedSession ? restartVideoIcon : playVideoIcon} style={{width:"100%"}} />
            </Box>
          </Box>
        </Box>
        {/* ⛔ LE CTA EST SOUS LA VIDEO, PAS EN BAS DE PAGE.
            Les planches 01 — VIDEO le posent sur la meme rangee que la legende,
            immediatement sous le lecteur et ferre a droite. Sur LAB il vivait
            dans la barre du bas, a plus de 300 px du lecteur et separe de lui
            par du vide. La video n'a d'ailleurs AUCUNE bande basse sur la
            planche : la page est blanche jusqu'en bas. */}
        {/* ⛔ RANGEE SOUS LE LECTEUR — node 01. A gauche la legende sur fond
            blanc, a droite le CTA. Sur Tablet et Mobile la planche empile et
            centre les deux. */}
        <Box sx={{
          /* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 3.2 : VISIBLE DANS LA
             SLIDE DE L'EDITEUR. L'editeur rend la slide en `onlyContent`, ce qui
             masquait cette rangee : titre, texte et duree n'y etaient ni visibles
             ni editables. En editeur, elle s'affiche (meme regle que le lecteur
             plus haut, `onlyContent !== true || isEditor === true`). Les
             vignettes (plan, barre laterale) restent sans elle, et l'apprenant
             n'a jamais `isEditor` : rien ne change pour lui. Ecriture inchangee :
             EditableField puis `onSequenceChange`, comme les autres champs. */
          display: (onlyContent === true && isEditor !== true) ? "none" : "flex",
          width:"100%",
          /* La rangee legende + CTA suit le lecteur : meme borne, sinon elle
             s'elargit toute seule sur grand ecran et le CTA quitte le bord
             droit de la video. */
          maxWidth: `min(${LARGEUR_VIDEO_PLANCHE}px, ${PART_LARGEUR_VIDEO * 100}cqw)`,
          alignSelf:"center",
          flexDirection:["column", "column", "row", "row"],
          alignItems:["center", "center", "flex-end", "flex-end"],
          justifyContent:"space-between",
          gap:["12px", "16px", "24px", "24px"],
          flexShrink:0,
          marginTop:["16px", "20px", "24px", "24px"],
          color:"text.black",
          textAlign:["center", "center", "left", "left"],
        }}>
          <Box sx={{ minWidth:0 }}>
              <Box sx={{
                display:"flex",
                flexDirection:"column",
                gap:["10px", "10px", "10px", "15px"],
                overflow:"hidden",
                userSelect:"none",
                /* L53 demandait ces infos « en bas » DANS le lecteur. Elles ne
                   sont plus dedans : la rangee qui les porte gere desormais son
                   alignement, et cette ligne n'a plus d'objet. */
              }}>
                <EditableField
                  field={format?.title}
                  type="title"
                  isEditor={isEditor}
                  sx={{
                    // Mesure sur l'artboard Figma remis a 1512 px : le titre y
                    // fait ~32px, pas 46. A 46px il concurrencait la video au
                    // lieu de la legender (cf ECARTS-THELAB.md, ecart 1).
                    /* ⚠️ TITRE DE LEGENDE RELU DANS FIGMA (21/09/2026, lot 3d).
                       Jeton « Text/Desktop/Heading/H3 » : 26 px / interligne 1,15
                       (noeud 3:20299 Desktop), 22 px en Mobile (3:40479). Tablet
                       DEDUIT a 26 : le noeud 3:40448 rend 17 px de haut avec
                       text-box-trim, exactement comme le Desktop en 26 px.
                       Le code montait a 32 px, soit 23 % de trop. */
                    fontSize:["22px", "26px", "26px", "26px"],
                    lineHeight:"1.15",
                    whiteSpace:"noWrap",
                    textOverflow:"ellipsis",
                    overflow:"hidden"
                  }}
                />
                <EditableField
                  field={format?.text}
                  type="text"
                  isEditor={isEditor}
                  sx={{
                    /* ⚠️ Jeton « Text/Desktop/Body/Large » : 20 px (3:20300),
                       15 px en Mobile (3:40480). La valeur etait PLATE a 22 px
                       aux trois paliers, soit +47 % sur Mobile. */
                    fontSize:["15px", "20px", "20px", "20px"],
                    overflow:"hidden",
                    /* Interligne 1,5 et non 1,3 : `leading-[1.5]` sur 3:20300 et
                       3:40480. Sur Mobile, ou la phrase passe a la ligne, les
                       20 points manquants tassaient le bloc. */
                    lineHeight:"1.5",
                    /* ⚠️ Couleur, pas opacite. Figma pose rgba(0,0,0,0.7) sur le
                       texte lui-meme ; le code posait du noir opaque attenue a
                       0,8, ce qui rend le bloc ENTIER translucide, fond compris
                       la ou il y en a un. Le secondaire etait 10 points trop
                       dense. */
                    color:"rgba(0, 0, 0, 0.7)",
                  }}
                />
                {/* ⛔ 04/10/2026 : la duree seulement si elle est saisie (ou en
                    editeur, pour la saisir). Sans elle, l'ecran affichait « min »
                    tout seul sous le texte (banc, Mobile). La planche 01 ne
                    dessine aucune duree (3:274, 3:40458). */}
                {(isEditor || String(format?.duration ?? "").trim() !== "") &&
                <Box sx={{ display:"flex", gap:"4px"}}>
                  <EditableField
                    field={format?.duration}
                    type="duration"
                    isEditor={isEditor}
                    sx={{
                      fontSize:"15px",
                      whiteSpace:"noWrap",
                      textOverflow:"ellipsis",
                      overflow:"hidden"
                    }}
                  />
                  <Box sx={{
                    fontSize:"15px",
                    whiteSpace:"noWrap",
                    textOverflow:"ellipsis",
                    overflow:"hidden"
                  }}>
                    {`min`}
                  </Box>
                </Box>
                }
              </Box>
          </Box>

          <EditableButton
            isEditor={false}
            disabled={!passageLibre}
            type="button"
            field={languageValues?.formation?.formatVideo?.buttonNext[actLanguage]}
            sx={{}}
            onClick={() => {
              if (isEditor !== true) {
                manageFormatResponse(format, [{ key: 'isDone', value: true }])
                manageSlide(1)
              }
            }}
          />
        </Box>
      </CenterContainer>
      {/* Pas de `BottomBar` : la planche ne montre aucune bande basse sur la
          video, et le CTA est remonte sous le lecteur. */}
    </Box>
  )
}


export default FormatVideo
